/**
 * The translation guard (DESIGN ED-162, ED-163): the deterministic check every attempt of a translation-scope run (repair
 * mode) passes before it can be accepted, kept as best or kept by the lenient tier. It reads the TR-01 and TR-02 lexicons
 * (`tr-scan.ts`, the channels' own) on the unit's paragraphs before and after the attempt:
 *
 *  (a) no INTRODUCED replaceable strong TR-01 marker: for a gram of the machine lexicon at or above the strong cut that is
 *      replaceable (`replaceable`: a bigram, or a word outside the frame-stop list, ED-166), its count in the unit's
 *      paragraphs after the attempt must not exceed the count before;
 *  (b) no LOST replaceable strong TR-02 marker: the same for the human lexicon, in the other direction (TR-02 is protected,
 *      never a target, and never asked for), except the occurrences that lay inside a targeted TR-01 marker the attempt
 *      replaced (the "as" of "such as");
 *  (c) the WEIGHT guard (ED-166): with K over all grams of a side (log1p, exactly the channel's K_p) and E the prose words,
 *        ΔK₁ ≤ max(0, r₁·ΔE) + TOL     and     ΔK₂ ≥ min(0, r₂·ΔE) − X − TOL
 *      where r₁, r₂ are the BASELINE document's K/E per side and X the human weight of the replaced targets' own words
 *      (as in (b)). Machine-leaning weight never grows beyond what added words carry at the baseline rate, and human-leaning
 *      weight never shrinks beyond what removed words carry, so each accepted change moves the document's TR-01 rate up,
 *      and TR-02's down, only through words removed that carried no marker (a length effect: at most the factor
 *      E/(E − D) for D net words removed). The bound is per unit against fixed baseline rates, so it holds in whatever
 *      order the units are applied (ED-107). The exact rate form (ΔK₁ ≤ r₁·ΔE) would refuse every deletion of a word
 *      outside the lexicon, MT-01's removal of a duplicated word among them.
 *
 * A side is checked only when its channel scored at baseline. Pure and deterministic; no state beyond a bounded cache of
 * the per-text scores. The measure of a paragraph is that of the channels: the paragraph is built into a Doc of its own
 * (`buildDoc(ingestTxtSync(text))`, the same normalisation as the engine), E is its prose `ownWords` and K its
 * `trScoreParagraph` scores, so a paragraph's K and E equal what a re-ingest of the edited document gives.
 *
 * The same module reports ED-163's success measure for a unit that targets TR-01: addressed when every targeted marker is
 * gone and the unit's K₁ fell, partial when K₁ fell with markers surviving, none otherwise.
 */
import type { CoreAnalysis } from '../analyse.ts';
import { proseParagraphs } from '../channels/helpers.ts';
import { type TrSide, trLexicon, trScan, trStrongCut } from '../channels/tr-scan.ts';
import { buildDoc } from '../document/build.ts';
import { ingestTxtSync } from '../ingest/index.ts';
import { loadEnglishLexicons } from '../lang/en/lexicons/index.ts';
import { trScoreParagraph, trTokens } from '../lang/en/tr-grams.ts';
import { TOL } from './floors.ts';

/** The tolerance of the rate guard (the engine's `TOL`). */
export const TR_TOL = TOL;
/** Grams named in one feedback line. */
const MAX_NAMED = 5;
/** Grams named in a rule-(a) or rule-(b) line. */
const MAX_LISTED = 8;
/** Texts kept in the per-text score cache before it is cleared. */
const CACHE_MAX = 20000;

export const TR_SIDES: readonly TrSide[] = ['machine', 'human'];
/** The channel each side of the lexicon belongs to. */
export const TR_CHANNEL: Readonly<Record<TrSide, 'TR-01' | 'TR-02'>> = {
	machine: 'TR-01',
	human: 'TR-02',
};

/** What one paragraph body scores: prose words, K per side, and per-gram counts for the lexicon grams it holds. */
interface BodyScore {
	E: number;
	K: Record<TrSide, number>;
	/** Occurrences (summed over the body's prose paragraphs) of each lexicon gram. */
	count: Record<TrSide, Map<string, number>>;
	/** Σ over the body's paragraphs of log1p(count): the gram's term in K is weight × this. */
	log: Record<TrSide, Map<string, number>>;
}

/** The run's constants: the baseline rates, the language and which paragraphs count (prose at baseline). */
export interface TrGuardEnv {
	/** The sides to check: those whose channel scored at baseline with prose words. */
	sides: readonly TrSide[];
	/** The baseline document's K/E per side. */
	rate: Partial<Record<TrSide, number>>;
	language: string;
	/** Prose paragraphs of the baseline document (`proseParagraphs`): the others carry no K and no E, as in the channels. */
	prose: ReadonlySet<number>;
	/** Per-text scores (bounded; filled as attempts are checked). */
	cache: Map<string, BodyScore>;
}

/** The guard's environment for a baseline analysis; undefined when neither TR channel scored. */
export function makeTrGuardEnv(core: CoreAnalysis): TrGuardEnv | undefined {
	const sides: TrSide[] = [];
	const rate: Partial<Record<TrSide, number>> = {};
	const scored = (c: 'TR-01' | 'TR-02'): boolean => core.byId[c]?.status === 'scored';
	if (!scored('TR-01') && !scored('TR-02')) return undefined;
	const scan = trScan(core.doc);
	for (const side of TR_SIDES) {
		if (!scored(TR_CHANNEL[side])) continue;
		const { K, E } = scan[side];
		if (!(E > 0)) continue;
		sides.push(side);
		rate[side] = K / E;
	}
	if (sides.length === 0) return undefined;
	return {
		sides,
		rate,
		language: core.doc.language.code,
		prose: new Set(proseParagraphs(core.doc)),
		cache: new Map(),
	};
}

const LEX: Partial<Record<TrSide, Map<string, number>>> = {};
const lexicon = (side: TrSide): Map<string, number> => {
	let l = LEX[side];
	if (!l) {
		l = trLexicon(side);
		LEX[side] = l;
	}
	return l;
};

/** The lexicon weight of a gram on a side (0 when absent). */
const weightOf = (side: TrSide, gram: string): number => lexicon(side).get(gram) ?? 0;

let stopWords: ReadonlySet<string> | undefined;
/**
 * A marker a rewrite can replace (ED-166): a bigram, or a single word outside the frame-stop list (articles, pronouns,
 * auxiliaries, the commonest prepositions: `pack-process.json#frameStop`). The strong lists hold such words ("it", "at",
 * "had" on the machine side, "to", "was" on the human side); asking for them to be replaced or kept word for word would
 * contort every sentence, so they are carried by the weight guard (c) in aggregate, never quoted, targeted or counted one by one.
 */
export function replaceable(gram: string): boolean {
	if (gram.includes(' ')) return true;
	stopWords ??= new Set(loadEnglishLexicons().processPack.lists.frameStop ?? []);
	return !stopWords.has(gram);
}

/** The grams inside a gram's own words: "such as" → "such", "as", "such as"; a word → itself. */
function subgrams(gram: string): string[] {
	const t = gram.split(' ');
	return t.length === 1 ? [gram] : [...t, gram];
}

const emptyScore = (): BodyScore => ({
	E: 0,
	K: { machine: 0, human: 0 },
	count: { machine: new Map(), human: new Map() },
	log: { machine: new Map(), human: new Map() },
});

/** The score of one paragraph body (see the module header). */
export function scoreBody(env: Pick<TrGuardEnv, 'language' | 'cache'>, text: string): BodyScore {
	const hit = env.cache.get(text);
	if (hit) return hit;
	const out = emptyScore();
	let doc: ReturnType<typeof buildDoc>;
	try {
		doc = buildDoc(ingestTxtSync(text), { language: env.language, headingLevelOf: () => 0 });
	} catch {
		// an unreadable body carries no prose
		env.cache.set(text, out);
		return out;
	}
	for (const p of proseParagraphs(doc)) {
		const b = doc.blocks[doc.paragraphs.block[p] as number];
		if (!b) continue;
		const t = doc.text.slice(b.charStart, b.charEnd);
		out.E += doc.paragraphs.ownWords[p] as number;
		for (const side of TR_SIDES) {
			const r = trScoreParagraph(t, lexicon(side));
			out.K[side] += r.k;
			const here = new Map<string, number>();
			for (const s of r.spans) here.set(s.gram, Math.max(here.get(s.gram) ?? 0, s.occurrence));
			for (const [g, c] of here) {
				out.count[side].set(g, (out.count[side].get(g) ?? 0) + c);
				out.log[side].set(g, (out.log[side].get(g) ?? 0) + Math.log1p(c));
			}
		}
	}
	if (env.cache.size >= CACHE_MAX) env.cache.clear();
	env.cache.set(text, out);
	return out;
}

/** The sum of the scores of several bodies (the unit's paragraphs). */
function sumBodies(
	env: TrGuardEnv,
	paras: readonly number[],
	bodies: readonly string[]
): BodyScore {
	const out = emptyScore();
	paras.forEach((p, i) => {
		if (!env.prose.has(p)) return;
		const s = scoreBody(env, bodies[i] as string);
		out.E += s.E;
		for (const side of TR_SIDES) {
			out.K[side] += s.K[side];
			for (const [g, c] of s.count[side]) out.count[side].set(g, (out.count[side].get(g) ?? 0) + c);
			for (const [g, c] of s.log[side]) out.log[side].set(g, (out.log[side].get(g) ?? 0) + c);
		}
	});
	return out;
}

/** The gram a quoted TR evidence span stands for (the channel's own tokenisation). */
export const gramOfSpan = (text: string): string => trTokens(text).join(' ');

/** A targeted TR-01 evidence item of the unit. */
export interface TrTarget {
	gram: string;
	/** The quoted text. */
	text: string;
	weight: number;
}

/** The facts of one side's rate guard. */
export interface TrRateCheck {
	side: TrSide;
	/** ΔK and ΔE of the unit's paragraphs, and the bound ΔK had to meet (≤ for the machine side, ≥ for the human side). */
	dK: number;
	dE: number;
	limit: number;
	ok: boolean;
	/** The grams that moved K the wrong way, the largest first. */
	grams: string[];
}

export interface TrOutcome {
	status: 'addressed' | 'partial' | 'none';
	kBefore: number;
	kAfter: number;
	/** Weight of the targeted evidence. */
	targeted: number;
	/** Quoted texts of the targeted markers still there. */
	surviving: string[];
}

export interface TrCheck {
	ok: boolean;
	/** (a) strong TR-01 grams whose count rose in the unit's paragraphs. */
	introduced: string[];
	/** (b) strong TR-02 grams whose count fell. */
	lost: string[];
	/** (c) per checked side. */
	rate: TrRateCheck[];
	rateOk: boolean;
	/** ED-163, for a unit that targets TR-01. */
	tr01?: TrOutcome;
	/** Feedback sentences for (b) and (c); (a) goes out as `introduced` spans. */
	lines: string[];
	/** The one-sentence reason a failed check gives. */
	reason?: string;
}

const quoteList = (xs: readonly string[]): string => xs.map((x) => `“${x}”`).join(', ');

/**
 * The guard on one attempt. `paras` are the unit's paragraph indices; `before` and `after` the paragraph BODIES (the editable
 * text without the attached structure) before and after the attempt, in the same order. `targets` (defined for a unit that
 * targets TR-01) are its TR-01 evidence items.
 */
export function checkTrAttempt(
	env: TrGuardEnv,
	paras: readonly number[],
	before: readonly string[],
	after: readonly string[],
	targets?: readonly TrTarget[]
): TrCheck {
	const b = sumBodies(env, paras, before);
	const a = sumBodies(env, paras, after);
	const dE = a.E - b.E;
	// the targeted TR-01 markers and how many of each the attempt replaced
	const need = new Map<string, { n: number; text: string }>();
	for (const t of targets ?? []) {
		const e = need.get(t.gram);
		if (e) e.n++;
		else need.set(t.gram, { n: 1, text: t.text });
	}
	// (b)'s and (c)'s exemption: the human grams inside a replaced target's own words, with the count replaced
	const exempt = new Map<string, number>();
	let exemptK = 0;
	for (const [g, { n }] of need) {
		const gone = Math.min(
			n,
			Math.max(0, (b.count.machine.get(g) ?? 0) - (a.count.machine.get(g) ?? 0))
		);
		if (gone === 0) continue;
		for (const h of subgrams(g)) {
			const w = weightOf('human', h);
			if (w <= 0) continue;
			exempt.set(h, (exempt.get(h) ?? 0) + gone);
			// an upper bound of the occurrences' share of K₂ (a first occurrence's share is w·ln 2)
			exemptK += gone * w * Math.LN2;
		}
	}
	const introduced: string[] = [];
	const lost: string[] = [];
	if (env.sides.includes('machine')) {
		const cut = trStrongCut('machine');
		for (const [g, c] of a.count.machine)
			if (weightOf('machine', g) >= cut && replaceable(g) && c > (b.count.machine.get(g) ?? 0))
				introduced.push(g);
		introduced.sort((x, y) => weightOf('machine', y) - weightOf('machine', x) || (x < y ? -1 : 1));
	}
	if (env.sides.includes('human')) {
		const cut = trStrongCut('human');
		for (const [g, c] of b.count.human)
			if (
				weightOf('human', g) >= cut &&
				replaceable(g) &&
				(a.count.human.get(g) ?? 0) < c - (exempt.get(g) ?? 0)
			)
				lost.push(g);
		lost.sort((x, y) => weightOf('human', y) - weightOf('human', x) || (x < y ? -1 : 1));
	}
	const rate: TrRateCheck[] = [];
	for (const side of env.sides) {
		const r = env.rate[side] as number;
		const dK = a.K[side] - b.K[side];
		const limit =
			side === 'machine' ? Math.max(0, r * dE) + TR_TOL : Math.min(0, r * dE) - exemptK - TR_TOL;
		const ok = side === 'machine' ? dK <= limit : dK >= limit;
		// the grams that moved K the wrong way: more weight on the machine side, less on the human side
		const moved: Array<[string, number]> = [];
		if (!ok) {
			const seen = new Set([...a.log[side].keys(), ...b.log[side].keys()]);
			for (const g of seen) {
				const d = weightOf(side, g) * ((a.log[side].get(g) ?? 0) - (b.log[side].get(g) ?? 0));
				if (side === 'machine' ? d > TR_TOL : d < -TR_TOL) moved.push([g, d]);
			}
			moved.sort(
				(x, y) => (side === 'machine' ? y[1] - x[1] : x[1] - y[1]) || (x[0] < y[0] ? -1 : 1)
			);
		}
		rate.push({ side, dK, dE, limit, ok, grams: moved.slice(0, MAX_NAMED).map((m) => m[0]) });
	}
	const rateOk = rate.every((r) => r.ok);
	const lines: string[] = [];
	if (lost.length > 0)
		lines.push(
			`The rewrite dropped wording of the original that must stay as it is: ${quoteList(lost.slice(0, MAX_LISTED))}. Keep it exactly as in the original.`
		);
	for (const r of rate) {
		if (r.ok) continue;
		if (r.side === 'machine')
			lines.push(
				r.grams.length > 0
					? `The rewrite uses more word choices that machine translation favours than the original did (${quoteList(r.grams)}). Use plainer wording, or keep the original's.`
					: 'The rewrite uses more word choices that machine translation favours than the original did. Keep the original wording outside the quoted passages.'
			);
		else
			lines.push(
				r.grams.length > 0
					? `The rewrite dropped natural wording of the original (${quoteList(r.grams)}). Keep the original's wording there.`
					: 'The rewrite dropped natural wording of the original. Keep the original wording outside the quoted passages.'
			);
	}
	const ok = introduced.length === 0 && lost.length === 0 && rateOk;
	const out: TrCheck = { ok, introduced, lost, rate, rateOk, lines };
	if (!ok) {
		out.reason =
			introduced.length > 0
				? `the rewrite introduced the translation-favoured wording ${quoteList(introduced.slice(0, 3))}`
				: lost.length > 0
					? `the rewrite dropped wording that must stay (${quoteList(lost.slice(0, 3))})`
					: 'the rewrite moved the passage toward the translation side (the weight guard)';
	}
	if (targets && env.sides.includes('machine')) {
		const surviving: string[] = [];
		for (const [g, { n, text }] of need)
			if ((a.count.machine.get(g) ?? 0) > (b.count.machine.get(g) ?? 0) - n) surviving.push(text);
		const fell = a.K.machine - b.K.machine < -TR_TOL;
		out.tr01 = {
			status: fell ? (surviving.length === 0 ? 'addressed' : 'partial') : 'none',
			kBefore: b.K.machine,
			kAfter: a.K.machine,
			targeted: targets.reduce((s, t) => s + t.weight, 0),
			surviving,
		};
	}
	return out;
}

/**
 * The replaceable strong TR markers of a text on a side (ED-166), in text order, without repeats (by gram): the quoted text
 * of the first occurrence. With `limit`, only the `limit` heaviest grams (ties to the earlier one), still in text order.
 */
export function strongMarkers(text: string, side: TrSide, limit?: number): string[] {
	const cut = trStrongCut(side);
	const seen = new Set<string>();
	const found: Array<{ text: string; weight: number }> = [];
	for (const s of trScoreParagraph(text, lexicon(side)).spans) {
		if (s.weight < cut || !replaceable(s.gram) || seen.has(s.gram)) continue;
		seen.add(s.gram);
		found.push({ text: text.slice(s.start, s.end), weight: s.weight });
	}
	if (limit === undefined || found.length <= limit) return found.map((f) => f.text);
	const keep = new Set(
		found
			.map((f, i) => ({ i, w: f.weight }))
			.sort((a, b) => b.w - a.w || a.i - b.i)
			.slice(0, limit)
			.map((x) => x.i)
	);
	return found.filter((_, i) => keep.has(i)).map((f) => f.text);
}

/**
 * The `n` heaviest replaceable markers of a side's lexicon, heaviest first (ties by gram). The TR-01 directive names the
 * machine side's 20 (ED-161's trigger, met by the pilot: more than a third of the versions introduced a marker).
 */
export function heaviestReplaceable(side: TrSide, n: number): string[] {
	return [...lexicon(side)]
		.filter(([g]) => replaceable(g))
		.sort((x, y) => y[1] - x[1] || (x[0] < y[0] ? -1 : 1))
		.slice(0, n)
		.map(([g]) => g);
}
